import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Custom hook for managing CRM lead notes
 */
export function useCrmNotes(leadId, refetch) {
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/lead-notes`, { lead_id: leadId, content: newNote });
      toast.success('Note added — analyzing for follow-up tasks…');
      setNewNote('');
      refetch();
      // The backend extracts tasks via LLM in the background (~5-15s).
      // Refetch once more after a short delay so any auto-generated tasks appear without a manual reload.
      setTimeout(() => { refetch(); }, 12000);
    } catch {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };
  
  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/lead-notes/${noteId}`);
      toast.success('Note deleted');
      refetch();
    } catch {
      toast.error('Failed to delete note');
    }
  };

  const handleEditNote = async (noteId, content) => {
    const trimmed = (content || '').trim();
    if (!trimmed) {
      toast.error('Note cannot be empty');
      return false;
    }
    try {
      await axios.put(`${API}/lead-notes/${noteId}`, { content: trimmed });
      toast.success('Note updated');
      refetch();
      return true;
    } catch {
      toast.error('Failed to update note');
      return false;
    }
  };

  return {
    newNote,
    setNewNote,
    addingNote,
    handleAddNote,
    handleDeleteNote,
    handleEditNote,
  };
}

/**
 * Custom hook for managing CRM lead tasks
 */
export function useCrmTasks(leadId, refetch) {
  const [taskForm, setTaskForm] = useState({ description: '', due_date: '', task_type: 'task' });
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleCreateTask = async () => {
    if (!taskForm.description.trim()) {
      toast.error('Description is required');
      return;
    }
    setSaving(true);
    try {
      // Convert datetime-local (local time) to UTC ISO string so the scheduler
      // can compare it correctly regardless of server timezone.
      const dueUtc = taskForm.due_date
        ? new Date(taskForm.due_date).toISOString()
        : null;
      await axios.post(`${API}/tasks`, {
        lead_id: leadId,
        description: taskForm.description,
        due_date: dueUtc,
        task_type: taskForm.task_type || 'task',
      });
      const isScheduled = ['call', 'meeting'].includes(taskForm.task_type);
      toast.success(isScheduled ? 'Task created & calendar event added' : 'Task created');
      setTaskDialogOpen(false);
      setTaskForm({ description: '', due_date: '', task_type: 'task' });
      refetch();
    } catch {
      toast.error('Failed to create task');
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggleTask = async (taskId, currentStatus) => {
    try {
      const newStatus = currentStatus ? 'pending' : 'completed';
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus, completed: !currentStatus });
      toast.success(currentStatus ? 'Task reopened' : 'Task completed');
      refetch();
    } catch { 
      toast.error('Failed to update task'); 
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Task deleted');
      refetch();
    } catch { 
      toast.error('Failed to delete task'); 
    }
  };
  
  return {
    taskForm,
    setTaskForm,
    taskDialogOpen,
    setTaskDialogOpen,
    saving,
    handleCreateTask,
    handleToggleTask,
    handleDeleteTask,
  };
}

export default { useCrmNotes, useCrmTasks };
